import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { Invitation, OrganisationMember } from '@/common/types/organisation';
import TableInitialsCircle from '@/common/ui/TableInitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import {
  CheckIcon,
  DotsThreeVerticalIcon,
  PaperPlaneRightIcon,
  TrashIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { TableCell, TableRow } from '@shadcn/components/table';
import { useState } from 'react';
import { toast } from 'sonner';

interface OrganisationMemberRowProps {
  member: OrganisationMember;
  isAdmin: boolean;
  isCurrentUserAdmin: boolean;
  onRoleChange: (userId: string, role: 'admin' | 'member') => void;
  onRemoveMember: () => void;
}
export function OrganisationMemberRow({
  member,
  isAdmin,
  isCurrentUserAdmin,
  onRoleChange,
  onRemoveMember,
}: OrganisationMemberRowProps) {
  const { organisation } = useOrganisation();

  const currentUserId = useAppSelector(selectUserId);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  function handleRoleChange(userId: string, role: 'admin' | 'member') {
    if (!organisation) {
      console.error('Missing org ID');
      toast.error('Failed to update role', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    const numRemainingAdmins = organisation.members.filter(
      (m) => m.uid !== userId && m.role === 'admin',
    ).length;
    if (role === 'member' && numRemainingAdmins === 0) {
      toast.error('Failed to update role', {
        description: 'You must have at least one admin',
      });
      return;
    }
    onRoleChange(userId, role);
  }

  function handleRemoveMember(userId: string) {
    if (userId === currentUserId) {
      toast.error('Failed to remove member', {
        description: 'You cannot remove yourself',
      });
      return;
    }
    onRemoveMember();
  }

  return (
    <TableRow>
      <TableCell className="w-4"></TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <TableInitialsCircle
            userName={member.name || member.email}
            size={24}
          />
          {member.name}
        </div>
      </TableCell>
      <TableCell>{member.email}</TableCell>
      <TableCell>{isAdmin ? 'Admin' : 'Member'}</TableCell>
      <TableCell className="flex justify-end mr-8">
        {isCurrentUserAdmin && member.uid !== currentUserId ? (
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-1/2 mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="bottom"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuLabel>{member.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <UserCircleIcon size={16} />
                  <span className="ml-2">Change role</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      className="flex items-center justify-between"
                      onClick={() => handleRoleChange(member.uid, 'admin')}
                    >
                      <span>Admin</span>
                      {isAdmin && <CheckIcon size={16} />}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="flex items-center justify-between"
                      onClick={() => handleRoleChange(member.uid, 'member')}
                    >
                      <span>Member</span>
                      {!isAdmin && <CheckIcon size={16} />}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setIsDropdownOpen(false);
                  handleRemoveMember(member.uid);
                }}
              >
                <TrashIcon weight="bold" size={16} color="red" /> Remove member
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="h-[36px]" />
        )}
      </TableCell>
    </TableRow>
  );
}

interface InviteRowProps {
  invite: Invitation;
  isCurrentUserAdmin: boolean;
  handleResendInvite: (email: string) => void;
  handleDeleteInvite: (token: string) => void;
}

export function InviteRow({
  invite,
  isCurrentUserAdmin,
  handleResendInvite,
  handleDeleteInvite,
}: InviteRowProps) {
  return (
    <TableRow>
      <TableCell className="w-4"></TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <TableInitialsCircle userName={invite.email} size={24} />
          <span className="text-neutral-400">—</span>
        </div>
      </TableCell>
      <TableCell className="text-neutral-400">{invite.email}</TableCell>
      <TableCell>
        <span className="text-yellow-600">Pending</span>
      </TableCell>
      <TableCell className="flex justify-end mr-8">
        {isCurrentUserAdmin ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-1/2 mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="bottom"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuLabel>{invite.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => handleResendInvite(invite.email)}
              >
                <PaperPlaneRightIcon size={16} /> Resend invite
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => handleDeleteInvite(invite.token)}
              >
                <TrashIcon weight="bold" size={16} color="red" /> Revoke invite
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="h-[36px]" />
        )}
      </TableCell>
    </TableRow>
  );
}
